import { MigrationProvider } from 'src/types/TMigration';

/**
 * The providers a customer can migrate from, in one place.
 *
 * Adding a provider the dashboard should offer is one entry here plus the
 * translator and lister for it in migration-onramp; nothing else on the page
 * knows the list. `offered: false` keeps a provider migration-onramp supports
 * out of the cards without losing its name, which the page still needs to show
 * for an account an operator configured from the command line.
 */
export type ProviderInfo = {
  id: MigrationProvider;
  name: string;
  /** One line under the name on the card: the promise, in the customer's terms. */
  blurb: string;
  /** What "Your images are at" should look like for this vendor. */
  placeholder: string;
  /** Shown as a card on the un-enrolled page. */
  offered: boolean;
};

export const PROVIDERS: ProviderInfo[] = [
  {
    id: 'cloudinary',
    name: 'Cloudinary',
    blurb: 'Keep every res.cloudinary.com URL working, transformations included, while your library copies across.',
    placeholder: 'https://res.cloudinary.com/your-cloud-name',
    offered: true,
  },
  {
    id: 'imagekit',
    name: 'ImageKit',
    blurb: 'Keep every ik.imagekit.io URL working, tr= parameters included, while your library copies across.',
    placeholder: 'https://ik.imagekit.io/your-endpoint-id',
    offered: true,
  },
  // Supported by migration-onramp and configurable by an operator, but not a
  // dashboard choice: there are no vendor parameters to translate and no library
  // to list, so it is not the same kind of decision as the two above. See
  // `MigrationProvider` in src/types/TMigration.ts.
  {
    id: 'webserver',
    name: 'A web server',
    blurb: 'Plain files on a web server of your own.',
    placeholder: 'https://images.example.com',
    offered: false,
  },
];

export const OFFERED_PROVIDERS = PROVIDERS.filter((p) => p.offered);

export function providerInfo(id: MigrationProvider | string | undefined): ProviderInfo | undefined {
  return PROVIDERS.find((p) => p.id === id);
}

/** Display name for a provider id, falling back to the id itself. */
export function providerName(id: MigrationProvider | string | undefined): string {
  return providerInfo(id)?.name || (id ?? '');
}
